import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import isEqual from 'lodash/isEqual';
import { useSearchParams } from 'react-router-dom';
import { CandidateFilterType } from '@/components/CandidateFilter/CandidateFilter.types';
import useApiFetch from '@/hooks/useApiFetch';
import candidatesServices, { CandidatesResponse } from '@/services/candidates';
import { chakraExtendedTheme } from '@/styles/theme';
import { FetchApiIds, LocalStorageItemKeys } from '@/types';
import CandidatesListComponent from './CandidatesList.component';
import { DEFAULT_PAGINATION_DATA, DEFAULT_SORT_BY } from './CandidatesList.constants';
import {
  getPaginationDataFromLocalStorage,
  setPaginationDataToLocalStorage,
} from './CandidatesList.utils';
import { PaginationData } from '../JobsList/JobsList.utils';

const CandidatesListPage = function (): JSX.Element {
  const [searchParams] = useSearchParams();

  const parseFilters = useCallback(
    (serializedFilters: string | null, hashValue = ''): CandidateFilterType[] => {
      if (!serializedFilters) {
        return [];
      }

      const parseFiltersValue = (valueToParse: string): CandidateFilterType[] => {
        const parsedFilters = JSON.parse(valueToParse);
        return Array.isArray(parsedFilters) ? (parsedFilters as CandidateFilterType[]) : [];
      };

      try {
        return parseFiltersValue(serializedFilters);
      } catch {
        if (!hashValue) {
          return [];
        }

        try {
          return parseFiltersValue(`${serializedFilters}#${hashValue.replace(/^#/, '')}`);
        } catch {
          return [];
        }
      }
    },
    []
  );

  const getFiltersFromUrl = useCallback(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const savedFiltersOnURL = urlParams.get('filters');
    return parseFilters(savedFiltersOnURL, window.location.hash);
  }, [parseFilters]);

  const getFiltersFromLocalStorage = useCallback(() => {
    const fromLocalstorage = window.localStorage.getItem(LocalStorageItemKeys.FILTERS);
    return parseFilters(fromLocalstorage);
  }, [parseFilters]);

  const combineFiltesFromUrlAndLocalStorage = useCallback(() => {
    const filtersFromUrl = getFiltersFromUrl();
    const filtersFromLocalStorage = getFiltersFromLocalStorage();
    const allFilters = [...filtersFromUrl, ...filtersFromLocalStorage];

    const combinedFilters = new Map(allFilters.map((item) => [item.field, item])).values();
    return [...combinedFilters] as CandidateFilterType[];
  }, [getFiltersFromUrl, getFiltersFromLocalStorage]);

  const [preDefinedFilters, setPreDefinedFilters] = useState<CandidateFilterType[]>(
    combineFiltesFromUrlAndLocalStorage()
  );
  const [showFilters, setShowFilters] = useState(false);
  const [isAddingFilters, setIsAddingFilters] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState('');
  const [isNewCandidateFormOpened, setIsNewCandidateFormOpened] = useState(
    searchParams.get('new') === 'true'
  );
  const [pagination, setPagination] = useState<PaginationData>(DEFAULT_PAGINATION_DATA);

  const onSelectFilters = (filters): void => {
    setSelectedFilters(filters);
  };

  const isAddingNewUser = useRef(false);

  const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);

  const onUpdateSortBy = (event): void => {
    const {
      target: { value, name },
    } = event;
    setSortBy((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    const paginationData = getPaginationDataFromLocalStorage();

    if (paginationData) {
      setPagination(paginationData);
    }
  }, []);

  useEffect(() => {
    const isDefaultSortBy = isEqual(sortBy, DEFAULT_SORT_BY);
    const isDefaultSelectedFilters = !selectedFilters;

    if (!isDefaultSortBy || !isDefaultSelectedFilters) {
      setPagination((prev) => ({
        ...prev,
        currentPage: 1,
      }));
    }
  }, [selectedFilters, sortBy]);

  useEffect(() => {
    const isDefaultData = isEqual(pagination, DEFAULT_PAGINATION_DATA);

    if (!isDefaultData) {
      setPaginationDataToLocalStorage(pagination);
    }
  }, [pagination]);

  const candidatesFilteredResponse = useApiFetch<CandidatesResponse>(
    [
      FetchApiIds.CANDIDATES_FILTERED,
      sortBy.order,
      sortBy.column,
      pagination.currentPage,
      pagination.itemsPerPage,
      selectedFilters,
    ],
    async () =>
      candidatesServices.getFilteredCandidates({
        take: pagination.itemsPerPage,
        page: pagination.currentPage,
        column: sortBy.column,
        order: sortBy.order,
        filters: selectedFilters,
      }),
    {
      enabled: !!selectedFilters,
      retry: 1,
    }
  );

  const candidatesNotFilteredResponse = useApiFetch<CandidatesResponse>(
    [
      FetchApiIds.CANDIDATES_LIST,
      sortBy.order,
      sortBy.column,
      pagination.currentPage,
      pagination.itemsPerPage,
    ],
    async () =>
      candidatesServices.getAllCandidates({
        take: pagination.itemsPerPage || DEFAULT_PAGINATION_DATA.itemsPerPage,
        page: pagination.currentPage,
        column: sortBy.column,
        order: sortBy.order,
      }),
    {
      enabled: !selectedFilters,
      retry: 1,
    }
  );

  const candidatesResponse = useMemo(() => {
    if (selectedFilters) {
      return candidatesFilteredResponse;
    }

    return candidatesNotFilteredResponse;
  }, [candidatesFilteredResponse, candidatesNotFilteredResponse, selectedFilters]);

  useEffect(() => {
    if (candidatesResponse.data?.currentPage) {
      setPagination((prev) => ({
        ...prev,
        count: candidatesResponse.data?.count,
        currentPage: candidatesResponse.data?.currentPage,
        lastPage: candidatesResponse.data.lastPage,
      }));
    }
  }, [
    candidatesResponse.data?.count,
    candidatesResponse.data?.currentPage,
    candidatesResponse.data?.lastPage,
  ]);

  const onChangePageHandler = (currentPage: number): void => {
    setPagination((prev) => ({
      ...prev,
      currentPage,
    }));
  };

  const onChangeRecordsPerPage = (itemsPerPage: number): void => {
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
      itemsPerPage,
    }));
  };

  const isAddingFiltersHandler = useCallback((isAdding: boolean): void => {
    setIsAddingFilters(isAdding);
  }, []);

  const onShowFiltersHandler = useCallback((): void => {
    setShowFilters((prev) => (isAddingFilters ? true : !prev));
  }, [isAddingFilters]);

  const onShowCandidateFormClick = (event): void => {
    event.stopPropagation();
    setIsNewCandidateFormOpened(true);
  };

  const onHideCandidateFormClick = (): void => {
    setIsNewCandidateFormOpened(false);
  };

  const onDirtyForm = useCallback((isDirty: boolean): void => {
    isAddingNewUser.current = isDirty;
  }, []);

  const onPageClick = useCallback(
    (event): void => {
      if (isAddingNewUser.current || !isNewCandidateFormOpened) {
        return;
      }

      const eventPath = event.composedPath() ?? [];

      const formId = 'new-candidate-form';
      const doesElementContainId = (element): boolean => {
        return element?.id?.includes(formId);
      };

      const isClickingInsideForm = eventPath.some(doesElementContainId);

      if (!isClickingInsideForm) {
        onHideCandidateFormClick();
      }
    },
    [isNewCandidateFormOpened]
  );

  useEffect(() => {
    window.addEventListener('click', onPageClick);
    return () => window.removeEventListener('click', onPageClick);
  }, [onPageClick]);

  useEffect(() => {
    const combinedFilters = combineFiltesFromUrlAndLocalStorage();
    setPreDefinedFilters(combinedFilters);
  }, [getFiltersFromLocalStorage, getFiltersFromUrl, combineFiltesFromUrlAndLocalStorage]);

  return (
    <ChakraProvider theme={chakraExtendedTheme}>
      <CandidatesListComponent
        {...pagination}
        {...sortBy}
        onAddNewCandidate={candidatesResponse.refetch}
        onSelectFilters={onSelectFilters}
        onDirtyForm={onDirtyForm}
        isNewCandidateFormOpened={isNewCandidateFormOpened}
        onShowCandidateFormClick={onShowCandidateFormClick}
        onHideCandidateFormClick={onHideCandidateFormClick}
        onUpdateSortBy={onUpdateSortBy}
        preDefinedFilters={preDefinedFilters}
        showFilters={showFilters || !!preDefinedFilters.length}
        isAddingFiltersHandler={isAddingFiltersHandler}
        onShowFiltersHandler={onShowFiltersHandler}
        onChangeRecordsPerPage={onChangeRecordsPerPage}
        isLoading={candidatesResponse.isLoading}
        onChangePageHandler={onChangePageHandler}
        candidatesList={candidatesResponse.data?.data ?? []}
      />
    </ChakraProvider>
  );
};

export default CandidatesListPage;
